import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { NO_LINE, leadLineOptions, leadLineValue } from "@/lib/lead-line";
import { useQueues, useSetTenantLeadLine, useTenantLeadLine } from "@/lib/queries";

/** Which line's Ready reps a new lead on no campaign is given to, in turn (ADR-184 in `platform`).
 *  An admin's: core-api refuses anyone else, and checks the line is the tenant's and enabled. */
export function NewLeadLine({ tenantId }: { tenantId: number }) {
  const toast = useToast();
  const setting = useTenantLeadLine(tenantId);
  const queues = useQueues();
  const save = useSetTenantLeadLine();
  const current = setting.data?.default_line_id ?? null;
  const options = leadLineOptions(queues.data ?? [], current);
  return (
    <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-border bg-surface px-4 py-3">
      <div>
        <p id="new-lead-line-label" className="text-sm font-medium text-text">
          New leads with no campaign
        </p>
        <p className="text-xs text-text-muted">
          A lead made with no campaign, as a contact typed into the console often is, goes to a rep Ready on this line,
          setters first, in turn.
        </p>
      </div>
      <Select
        value={current === null ? NO_LINE : String(current)}
        disabled={setting.isPending || queues.isPending || save.isPending}
        onValueChange={(next) =>
          save.mutate(
            { tenantId, lineId: leadLineValue(next) },
            {
              onSuccess: (saved) =>
                toast.success(saved.line_name ? `New leads with no campaign go to ${saved.line_name}` : "New leads with no campaign go to nobody"),
              onError: (e) => toast.error("Could not change the line", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      >
        <SelectTrigger className="w-56" aria-labelledby="new-lead-line-label">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} description={o.description} disabled={o.disabled}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
